import { useEffect, useRef, useState } from 'react';
import {
  Tabs, Tab, Paper, Table, TableHead, TableRow, TableCell, TableBody, Button,
  TextField, MenuItem, Stack, Typography, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  TablePagination, IconButton, Menu, ListItemIcon, ListItemText, Tooltip, Grid,
  Checkbox, FormControlLabel,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LockResetIcon from '@mui/icons-material/LockReset';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DashboardLayout from '../components/DashboardLayout';
import { navForRole } from '../components/officerNav';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const STAFF_ROLES = ['MDA_OFFICER', 'EVALUATOR', 'ADMIN', 'AUDITOR'];
const VENDOR_STATUSES = ['PENDING_REVIEW', 'APPROVED', 'SUSPENDED', 'BLACKLISTED'];
const STATUS_COLOR = { PENDING_REVIEW: 'warning', APPROVED: 'success', SUSPENDED: 'error', BLACKLISTED: 'error' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Turn an API error into a readable message, including field-level validation
// details (e.g. "password: Password must be at least 8 characters") when present.
function formatError(err, fallback) {
  const e = err.response?.data?.error;
  if (e?.details?.length) return e.details.map((d) => `${d.field}: ${d.message}`).join('; ');
  return e?.message || fallback;
}

// Per-row action menu. Each row owns its own anchor state, so opening one
// menu cannot leave another row's menu thinking it is open.
function RowActions({ user, onResetPassword, onToggleActive }) {
  const [anchor, setAnchor] = useState(null);
  const close = () => setAnchor(null);
  // Close the menu, then run the action on the next tick. Running it
  // synchronously means the action fires while the menu is still unmounting,
  // which drops state updates made during that window.
  const run = (fn) => () => { close(); setTimeout(() => fn(user), 0); };

  return (
    <>
      <Tooltip title="Actions">
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} aria-label={`Actions for ${user.fullName}`}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={run(onResetPassword)}>
          <ListItemIcon><LockResetIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Reset password</ListItemText>
        </MenuItem>
        <MenuItem onClick={run(onToggleActive)} sx={{ color: user.isActive ? 'error.main' : 'success.main' }}>
          <ListItemIcon>
            {user.isActive
              ? <BlockIcon fontSize="small" color="error" />
              : <CheckCircleOutlineIcon fontSize="small" color="success" />}
          </ListItemIcon>
          <ListItemText>{user.isActive ? 'Deactivate' : 'Activate'}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

export default function AdminConsole() {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState(null); // { msg, severity }
  const notify = (msg, severity = 'success') => setToast({ msg, severity });

  return (
    <DashboardLayout nav={navForRole(user?.role)} welcomeName={user?.fullName} title="Admin Console">
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Users" />
          <Tab label="MDAs" />
          <Tab label="Vendor Approvals" />
        </Tabs>
      </Paper>

      {tab === 0 && <UsersTab notify={notify} />}
      {tab === 1 && <MdasTab notify={notify} />}
      {tab === 2 && <VendorsTab notify={notify} />}

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}>
        <Alert severity={toast?.severity || 'success'} onClose={() => setToast(null)}>{toast?.msg}</Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

function UsersTab({ notify }) {
  const [users, setUsers] = useState([]);
  const [mdas, setMdas] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', fullName: '', role: 'MDA_OFFICER', mdaId: '' });
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmUser, setConfirmUser] = useState(null);
  const [page, setPage] = useState(0);          // MUI is 0-based, the API is 1-based
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  const load = async () => {
    const [u, m] = await Promise.all([
      api.get('/admin/users', { params: { page: page + 1, pageSize: rowsPerPage } }),
      api.get('/admin/mdas'),
    ]);
    setUsers(u.data.data);
    setTotal(u.data.meta?.total ?? u.data.data.length);
    setMdas(m.data.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [page, rowsPerPage]);

  const create = async () => {
    // Client-side guards for instant feedback before hitting the server.
    if (!form.fullName.trim()) return notify('Full name is required', 'error');
    if (!EMAIL_RE.test(form.email.trim())) return notify('A valid email is required', 'error');
    if (form.password.length < 8) return notify('Password must be at least 8 characters', 'error');
    if (form.role === 'MDA_OFFICER' && !form.mdaId) return notify('Select an MDA for this officer', 'error');
    try {
      await api.post('/admin/users', { ...form, email: form.email.trim(), fullName: form.fullName.trim() });
      setOpen(false);
      setForm({ email: '', password: '', fullName: '', role: 'MDA_OFFICER', mdaId: '' });
      notify('User created');
      // Newest first, so the new user is on page 1.
      if (page === 0) load(); else setPage(0);
    } catch (err) {
      notify(formatError(err, 'Failed to create user'), 'error');
    }
  };

  // Admin-set password. There is no self-service reset flow (SMTP is not
  // configured), so the admin sets a password and passes it to the user
  // out of band. The backend audits this as USER_PASSWORD_RESET.
  const submitReset = async () => {
    if (newPassword.length < 8) return notify('Password must be at least 8 characters', 'error');
    try {
      await api.post(`/admin/users/${resetUser.id}/reset-password`, { newPassword });
      notify(`Password reset for ${resetUser.fullName}`);
      setResetUser(null);
      setNewPassword('');
    } catch (err) {
      notify(formatError(err, 'Failed to reset password'), 'error');
    }
  };

  // Deactivating ends the user's sessions server-side. Accounts are never
  // deleted: officers stay attached to the tenders and audit entries they made.
  // Reactivating is harmless, so it goes straight through. Deactivating asks
  // first, via the dialog below rather than window.confirm.
  const toggleActive = (u) => {
    if (u.isActive) setConfirmUser(u);
    else applyActive(u, true);
  };

  const applyActive = async (u, next) => {
    try {
      await api.put(`/admin/users/${u.id}`, { isActive: next });
      notify(next ? `${u.fullName} reactivated` : `${u.fullName} deactivated`);
      setConfirmUser(null);
      load();
    } catch (err) {
      // Keep the dialog open on failure so the message is read in context.
      notify(formatError(err, 'Failed to update user'), 'error');
    }
  };

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" onClick={() => setOpen(true)}>Add user</Button>
      </Stack>
      <Paper>
        <Table>
          <TableHead><TableRow>
            <TableCell>Name</TableCell><TableCell>Email</TableCell><TableCell>Role</TableCell><TableCell>MDA</TableCell><TableCell>Active</TableCell><TableCell align="right">Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell>{u.fullName}</TableCell><TableCell>{u.email}</TableCell>
                <TableCell>{u.role.replace('_', ' ')}</TableCell><TableCell>{u.mda?.name || '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={u.isActive ? 'Active' : 'Inactive'}
                    color={u.isActive ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <RowActions
                    user={u}
                    onResetPassword={(target) => { setResetUser(target); setNewPassword(''); }}
                    onToggleActive={toggleActive}
                  />
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No users to show.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add staff user</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Full name" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} fullWidth />
            <TextField label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} fullWidth />
            <TextField label="Temporary password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} fullWidth helperText="At least 8 characters" />
            <TextField select label="Role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} fullWidth>
              {STAFF_ROLES.map((r) => <MenuItem key={r} value={r}>{r.replace('_', ' ')}</MenuItem>)}
            </TextField>
            {form.role === 'MDA_OFFICER' && (
              <TextField select label="MDA" value={form.mdaId} onChange={(e) => setForm({ ...form, mdaId: e.target.value })} fullWidth>
                {mdas.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
              </TextField>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={create}>Create user</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!resetUser} onClose={() => setResetUser(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Setting a new password for {resetUser?.fullName} ({resetUser?.email}).
              Pass it to the user directly - it is not emailed.
            </Typography>
            <TextField
              label="New password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              helperText="At least 8 characters"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetUser(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitReset}>Reset password</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmUser} onClose={() => setConfirmUser(null)} fullWidth maxWidth="xs">
        <DialogTitle>Deactivate user</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {confirmUser?.fullName} ({confirmUser?.email}) will be signed out
            immediately and will not be able to log in. Their name stays on the
            tenders, evaluations and audit entries they made.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUser(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => applyActive(confirmUser, false)}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function MdasTab({ notify }) {
  const [mdas, setMdas] = useState([]);
  const [form, setForm] = useState({ name: '', code: '' });
  const [edit, setEdit] = useState(null);

  const load = async () => { const { data } = await api.get('/admin/mdas'); setMdas(data.data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post('/admin/mdas', form);
      setForm({ name: '', code: '' });
      notify('MDA created');
      load();
    } catch (err) {
      notify(formatError(err, 'Failed to create MDA'), 'error');
    }
  };

  // Correcting a mistyped MDA. Audited by the backend as MDA_UPDATE.
  const saveEdit = async () => {
    if (!edit.name.trim()) return notify('MDA name is required', 'error');
    try {
      await api.put(`/admin/mdas/${edit.id}`, { name: edit.name.trim(), code: edit.code.trim() });
      notify('MDA updated');
      setEdit(null);
      load();
    } catch (err) {
      notify(formatError(err, 'Failed to update MDA'), 'error');
    }
  };

  return (
    <>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField label="MDA name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
          <TextField label="Code (optional)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} sx={{ width: 200 }} />
          <Button variant="contained" onClick={create}>Add MDA</Button>
        </Stack>
      </Paper>
      <Paper>
        <Table>
          <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Code</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
          <TableBody>
            {mdas.map((m) => (
              <TableRow key={m.id}>
                <TableCell>{m.name}</TableCell>
                <TableCell>{m.code || '-'}</TableCell>
                <TableCell align="right">
                  <Button size="small" variant="outlined" onClick={() => setEdit({ id: m.id, name: m.name, code: m.code || '' })}>
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={!!edit} onClose={() => setEdit(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit MDA</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="MDA name"
              value={edit?.name || ''}
              onChange={(e) => setEdit({ ...edit, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Code (optional)"
              value={edit?.code || ''}
              onChange={(e) => setEdit({ ...edit, code: e.target.value })}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit}>Save changes</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// CAC verified mark. The reviewer compares the number with the CAC
// certificate (viewable below) or CAC's public search, corrects it if it
// differs, then ticks. Only verified numbers are published as official
// identifiers in the open contracting data; others are published by name.
function CacVerification({ detail, onChanged, notify }) {
  const [number, setNumber] = useState(detail.cacNumber || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNumber(detail.cacNumber || ''); }, [detail.id, detail.cacNumber]);
  const verified = !!detail.cacVerifiedAt;

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/vendors/${detail.id}/cac-verified`,
        next ? { verified: true, cacNumber: number } : { verified: false }
      );
      onChanged(data.data);
      notify(next ? 'CAC number marked as verified' : 'CAC verification removed');
    } catch (err) {
      notify(formatError(err, 'Could not update CAC verification'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Typography variant="subtitle2" color="text.secondary">CAC number</Typography>
      {verified ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography>{detail.cacNumber}</Typography>
          <Chip size="small" color="success"
            label={`Verified ${new Date(detail.cacVerifiedAt).toLocaleDateString('en-GB')}`} />
          <Button size="small" disabled={saving} onClick={() => save(false)}>Remove verification</Button>
        </Stack>
      ) : (
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <TextField size="small" label="As shown on the certificate" value={number}
              onChange={(e) => setNumber(e.target.value)} sx={{ width: 260 }} />
            <FormControlLabel
              control={<Checkbox checked={false} disabled={saving || !number.trim()} onChange={() => save(true)} />}
              label="CAC number verified"
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Compare with the CAC certificate below, or search.cac.gov.ng. Correct the number if it
            differs, then tick. Only verified numbers are published as official identifiers.
          </Typography>
        </Stack>
      )}
    </div>
  );
}

function VendorsTab({ notify }) {
  const [vendors, setVendors] = useState([]);
  const [filter, setFilter] = useState('PENDING_REVIEW');

  const load = async () => {
    const { data } = await api.get('/vendors', { params: filter ? { status: filter } : {} });
    setVendors(data.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (id, status) => {
    try {
      let reason;
      if (status === 'SUSPENDED' || status === 'BLACKLISTED') {
        reason = window.prompt(`Reason to ${status.toLowerCase()} this vendor?`);
        if (!reason) return;
      }
      await api.patch(`/vendors/${id}/status`, { status, reason });
      notify(`Vendor ${status.toLowerCase()}`);
      load();
    } catch (err) {
      notify(formatError(err, 'Action failed'), 'error');
    }
  };
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const viewVendor = async (id) => {
    setSelected(id);
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/vendors/${id}`);
      setDetail(data.data);
    } catch (err) {
      notify(formatError(err, 'Could not load vendor'), 'error');
      setSelected(null);
    } finally {
      setLoadingDetail(false);
    }
  };
  const closeDialog = () => { revokePreview(); setSelected(null); setDetail(null); };
  // The document is shown inside this dialog rather than a new tab: a
  // window.open after an await is not treated as user-initiated and gets
  // blocked silently, and the reviewer should see the certificate next to the
  // Approve button.
  const [preview, setPreview] = useState(null); // { url, mimeType, fileName }

  const previewRef = useRef(null);

  const revokePreview = () => {
    setPreview((p) => {
      if (p?.url) URL.revokeObjectURL(p.url);
      return null;
    });
  };

  // When the columns stack on a narrow screen the pane really is below the
  // list, so bring it into view. On wide screens it is already beside the
  // list and block: 'nearest' leaves the dialog where it is.
  useEffect(() => {
    if (preview && previewRef.current) {
      previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [preview]);

  const previewDoc = async (doc) => {
    revokePreview();
    try {
      // NOTE: download.routes is mounted at bare '/api', so there is no
      // '/downloads' segment. This path was wrong, which is why documents
      // never opened.
      const res = await api.get(`/compliance-documents/${doc.id}`, { responseType: 'blob' });
      const type = doc.mimeType || 'application/octet-stream';
      const url = URL.createObjectURL(new Blob([res.data], { type }));
      setPreview({ url, mimeType: type, fileName: doc.fileName, docId: doc.id });
    } catch (err) {
      notify(formatError(err, 'Could not open document'), 'error');
    }
  };
  const decide = async (status) => {
    await setStatus(selected, status);
    closeDialog();
  };

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <TextField select label="Filter" value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ width: 220 }}>
          <MenuItem value="">All</MenuItem>
          {VENDOR_STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
        </TextField>
      </Stack>
      <Paper>
        <Table>
          <TableHead><TableRow>
            <TableCell>Company</TableCell><TableCell>CAC</TableCell><TableCell>Contact</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {vendors.map((v) => (
              <TableRow key={v.id}>
                <TableCell>{v.companyName}</TableCell>
                <TableCell>
                  {v.cacNumber}
                  {v.cacVerifiedAt && <Chip size="small" color="success" label="Verified" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell>{v.user?.email}</TableCell>
                <TableCell><Chip size="small" label={v.status.replace('_', ' ')} color={STATUS_COLOR[v.status]} /></TableCell>
                <TableCell align="right">
                  <Button size="small" variant="outlined" onClick={() => viewVendor(v.id)}>View</Button>
                </TableCell>
              </TableRow>
            ))}
            {vendors.length === 0 && (
              <TableRow><TableCell colSpan={5}><Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No vendors in this category.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
      <Dialog open={!!selected} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>Vendor review</DialogTitle>
        <DialogContent dividers>
          {loadingDetail && <Typography color="text.secondary">Loading...</Typography>}
          {detail && (
            <Stack spacing={1.5}>
              <div>
                <Typography variant="subtitle2" color="text.secondary">Company</Typography>
                <Typography>{detail.companyName}</Typography>
              </div>
              <CacVerification detail={detail} notify={notify} onChanged={(v) => { setDetail(v); load(); }} />
              <div>
                <Typography variant="subtitle2" color="text.secondary">Contact</Typography>
                <Typography>{detail.user?.fullName || '-'}{' / '}{detail.user?.email || '-'}{' / '}{detail.user?.phone || '-'}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip size="small" label={detail.status.replace('_', ' ')} color={STATUS_COLOR[detail.status]} />
              </div>
              <div>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Compliance documents</Typography>
                {(detail.complianceDocs || []).length === 0 && <Typography color="text.secondary">No documents uploaded.</Typography>}
                {(detail.complianceDocs || []).length > 0 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <Stack spacing={0.5}>
                        {(detail.complianceDocs || []).map((d) => (
                          <Stack key={d.id} direction="row" justifyContent="space-between" alignItems="center"
                            sx={{
                              px: 1, py: 0.75, borderRadius: 1,
                              bgcolor: preview?.docId === d.id ? 'action.selected' : 'transparent',
                            }}>
                            <div>
                              <Typography variant="body2">{d.docType?.replace(/_/g, ' ') || 'Document'}</Typography>
                              <Typography variant="caption" color="text.secondary">{d.fileName}{' - '}{(d.sizeBytes / 1024).toFixed(0)} KB</Typography>
                            </div>
                            <Button size="small" variant={preview?.docId === d.id ? 'contained' : 'text'}
                              onClick={() => previewDoc(d)}>View</Button>
                          </Stack>
                        ))}
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <div ref={previewRef}>
                        {!preview && (
                          <Stack alignItems="center" justifyContent="center"
                            sx={{ minHeight: 240, border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                            <Typography variant="body2" color="text.secondary" align="center">
                              Select a document to view it here before deciding.
                            </Typography>
                          </Stack>
                        )}
                        {preview && (
                          <>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                              <Typography variant="subtitle2" color="text.secondary" noWrap sx={{ mr: 1 }}>
                                {preview.fileName}
                              </Typography>
                              <Stack direction="row" spacing={1} flexShrink={0}>
                                <Button size="small" component="a" href={preview.url} download={preview.fileName}>
                                  Download
                                </Button>
                                <Button size="small" onClick={revokePreview}>Close</Button>
                              </Stack>
                            </Stack>
                            {preview.mimeType.startsWith('image/') && (
                              <img src={preview.url} alt={preview.fileName}
                                style={{
                                  maxWidth: '100%', maxHeight: 440, objectFit: 'contain', display: 'block',
                                  margin: '0 auto', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 4,
                                }} />
                            )}
                            {preview.mimeType === 'application/pdf' && (
                              <iframe src={preview.url} title={preview.fileName}
                                style={{ width: '100%', height: 440, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 4 }} />
                            )}
                            {!preview.mimeType.startsWith('image/') && preview.mimeType !== 'application/pdf' && (
                              <Typography color="text.secondary">
                                This file type cannot be previewed. Use Download to open it.
                              </Typography>
                            )}
                          </>
                        )}
                      </div>
                    </Grid>
                  </Grid>
                )}
              </div>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {detail && detail.status !== 'APPROVED' && <Button color="success" onClick={() => decide('APPROVED')}>Approve</Button>}
          {detail && detail.status !== 'SUSPENDED' && <Button color="warning" onClick={() => decide('SUSPENDED')}>Suspend</Button>}
          {detail && detail.status !== 'BLACKLISTED' && <Button color="error" onClick={() => decide('BLACKLISTED')}>Blacklist</Button>}
          <Button onClick={closeDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}